import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import { ArrowLeft, Download, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { STATUS } from "@/lib/format";
import { downloadBlob } from "@/lib/pdf";

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
const team = (d) => [d.technician_name, ...d.helpers].filter(Boolean).join(", ") || "Da assegnare";
const fileBase = (r) => `rendiconto-${r.name.replace(/[^\w]+/g, "-").toLowerCase()}`;
const pdfSafe = (s) => String(s ?? "").replace(/[→–—]/g, "-").replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[^\x00-\xFF]/g, "");
const readLabel = (d) => d.work_plan ? (d.work_plan_read_at ? `letto da ${d.work_plan_read_by}` : "non ancora letto") : "";

function exportCsv(r) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Data", "Orario", "Squadra", "Programma assegnato", "Lettura programma", "Lavoro svolto", "Registrato da", "Ore", "Foto/video", "Stato"];
  const rows = r.days.map((d) => [fmt(d.date), `${d.start}-${d.end}`, team(d), d.work_plan, readLabel(d), d.done || d.cancel_reason, d.done_by, d.hours, d.media, STATUS[d.status]?.label || d.status]);
  downloadBlob(new Blob(["\ufeff" + [head, ...rows].map((l) => l.map(esc).join(";")).join("\n")], { type: "text/csv;charset=utf-8" }), `${fileBase(r)}.csv`);
}

function exportPdf(r, range) {
  const doc = new jsPDF();
  const T = doc.text.bind(doc);
  doc.text = (t, ...a) => T(Array.isArray(t) ? t.map(pdfSafe) : pdfSafe(t), ...a);
  const split = (t) => doc.splitTextToSize(pdfSafe(t), 180);
  let y = 18;
  const page = (need) => { if (y + need > 282) { doc.addPage(); y = 18; } };
  doc.setFont("helvetica", "bold").setFontSize(15).text(`Rendiconto cantiere: ${r.name}`, 14, y); y += 7;
  doc.setFont("helvetica", "normal").setFontSize(9).text([r.committente, [r.address, r.city].filter(Boolean).join(", "), range].filter(Boolean).join(" · "), 14, y); y += 6;
  doc.text(`Giorni: ${r.totals.dates} · Appuntamenti assegnati: ${r.totals.assigned} · Svolti: ${r.totals.done}${r.totals.cancelled ? ` · Annullati: ${r.totals.cancelled}` : ""}`, 14, y); y += 8;
  r.days.forEach((d) => {
    const plan = split(`Assegnato: ${d.work_plan || "-"}${d.work_plan ? ` (${readLabel(d)})` : ""}`);
    const done = split(`Svolto: ${d.done || (d.status === "annullato" ? `annullato${d.cancel_reason ? ` (${d.cancel_reason})` : ""}` : "-")}`);
    page(14 + (plan.length + done.length) * 4.5);
    doc.setDrawColor(220).line(14, y - 4, 196, y - 4);
    doc.setFont("helvetica", "bold").setFontSize(10).text(`${fmt(d.date)}  ${d.start}-${d.end}`, 14, y);
    doc.setFont("helvetica", "normal").setFontSize(9).text(`${STATUS[d.status]?.label || d.status}${d.hours ? ` · ${d.hours} h` : ""}`, 196, y, { align: "right" }); y += 5;
    doc.text(`Squadra: ${team(d)}${d.done_by ? ` · registrato da ${d.done_by}` : ""}`, 14, y); y += 5;
    doc.text(plan, 14, y); y += plan.length * 4.5;
    doc.text(done, 14, y); y += done.length * 4.5 + 6;
  });
  doc.save(`${fileBase(r)}.pdf`);
}

function DayRow({ d, i }) {
  return (
    <div className="grid gap-3 px-5 py-4 md:grid-cols-[180px_1fr_1fr]" data-testid={`report-day-${i}`}>
      <div>
        <p className="font-semibold capitalize text-slate-900">{fmt(d.date)}</p>
        <p className="text-sm text-slate-500">{d.start}–{d.end}</p>
        <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS[d.status]?.cls || ""}`} data-testid={`report-day-status-${i}`}>{STATUS[d.status]?.label || d.status}</span>
        <p className="mt-1 text-xs text-slate-500">{team(d)}</p>
      </div>
      <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Lavoro assegnato</p>
        <p className="whitespace-pre-line text-sm text-slate-700" data-testid={`report-day-plan-${i}`}>{d.work_plan || "—"}</p>
        {d.work_plan && <p className={`mt-1 text-xs ${d.work_plan_read_at ? "text-[#3d6b16]" : "text-amber-700"}`} data-testid={`report-day-read-${i}`}>{d.work_plan_read_at ? `Letto da ${d.work_plan_read_by}` : "Non ancora letto dal responsabile"}</p>}</div>
      <div><p className="text-xs font-semibold uppercase tracking-wide text-[#3d6b16]">Lavoro svolto</p>
        <p className="whitespace-pre-line text-sm text-slate-700" data-testid={`report-day-done-${i}`}>{d.done || (d.status === "annullato" ? `Annullato${d.cancel_reason ? `: ${d.cancel_reason}` : ""}` : "Non ancora registrato")}</p>
        {(d.done_by || d.hours || d.media > 0) && <p className="mt-1 text-xs text-slate-500">{[d.done_by && `da ${d.done_by}`, d.hours && `${d.hours} h`, d.media > 0 && `${d.media} foto/video`].filter(Boolean).join(" · ")}</p>}</div>
    </div>
  );
}

export default function CantiereReport() {
  const { sid } = useParams();
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    api.get(`/admin/cantieri/${sid}/report`, { params: { date_from: range.date_from || undefined, date_to: range.date_to || undefined } })
      .then((res) => setR(res.data)).catch((e) => setErr(errMsg(e)));
  }, [sid, range]);
  useEffect(() => { load(); }, [load]);
  const rangeLabel = range.date_from || range.date_to ? `Periodo: ${range.date_from ? fmt(range.date_from) : "inizio"} - ${range.date_to ? fmt(range.date_to) : "fine"}` : "Tutto il cantiere";
  return (
    <div>
      <Link to="/admin/cantieri" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="report-back-link"><ArrowLeft className="h-4 w-4" /> Cantieri</Link>
      <PageHeader eyebrow="Rendiconto" title={r?.name || "Cantiere"}>
        <Button variant="outline" className="rounded-full" disabled={!r?.days.length} onClick={() => exportCsv(r)} data-testid="report-export-csv"><Download className="h-4 w-4" /> Excel (CSV)</Button>
        <Button className="rounded-full" disabled={!r?.days.length} onClick={() => exportPdf(r, rangeLabel)} data-testid="report-export-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border bg-white p-4">
        <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={range.date_from} onChange={(e) => setRange((p) => ({ ...p, date_from: e.target.value }))} data-testid="report-date-from" /></div>
        <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={range.date_to} onChange={(e) => setRange((p) => ({ ...p, date_to: e.target.value }))} data-testid="report-date-to" /></div>
        {(range.date_from || range.date_to) && <Button variant="ghost" size="sm" onClick={() => setRange({ date_from: "", date_to: "" })} data-testid="report-clear-range">Tutto il cantiere</Button>}
        {r && <p className="ml-auto text-sm text-slate-600" data-testid="report-totals">{r.totals.dates} giorni · {r.totals.assigned} appuntamenti attivi · <b className="text-[#3d6b16]">{r.totals.done} svolti</b>{r.totals.cancelled ? ` · ${r.totals.cancelled} annullati` : ""}</p>}
      </div>
      <div className="divide-y rounded-2xl border bg-white" data-testid="report-days">
        {err ? <p className="p-8 text-center text-sm text-red-600" data-testid="report-error">{err}</p>
          : r === null ? <p className="p-6 text-sm text-slate-500">Caricamento…</p>
          : !r.days.length ? <p className="p-8 text-center text-sm text-slate-500" data-testid="report-empty">Nessun giorno nel periodo scelto.</p>
          : r.days.map((d, i) => <DayRow key={d.id} d={d} i={i} />)}
      </div>
    </div>
  );
}
